import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCloudUploadAlt,
  FaFile,
  FaTimes,
  FaInfoCircle,
  FaArrowLeft,
  FaShieldAlt,
  FaRocket,
  FaCheckCircle,
  FaFilePdf,
  FaFileWord,
  FaFilePowerpoint,
  FaSpinner,
  FaUpload,
  FaStar,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const UploadNote = () => {
  const navigate = useNavigate();
  const { setShowFooter } = useFooter();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    subjectCode: "",
    branch: "",
    semester: "",
    module: "",
    tags: "",
  });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const modules = [1, 2, 3, 4, 5];

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");
  const getDropzoneBg = () =>
    isDarkMode ? "rgba(139,92,246,0.05)" : "rgba(139,92,246,0.03)";
  const getInfoBg = () =>
    isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.05)";

  const getFileIcon = () => {
    if (!file) return <FaCloudUploadAlt size={50} color="#8b5cf6" />;
    const fileType = file.type?.toLowerCase() || "";
    if (fileType.includes("pdf"))
      return <FaFilePdf size={50} color="#ef4444" />;
    if (fileType.includes("doc"))
      return <FaFileWord size={50} color="#3b82f6" />;
    if (fileType.includes("ppt"))
      return <FaFilePowerpoint size={50} color="#f59e0b" />;
    return <FaFile size={50} color="#8b5cf6" />;
  };

  const onDrop = (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile.size > 10 * 1024 * 1024) {
      toast.error("File size should be less than 10MB");
      return;
    }
    setFile(uploadedFile);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
    },
    maxFiles: 1,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64File = reader.result;

      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          "/api/upload/note",
          {
            fileData: base64File,
            ...formData,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        toast.success(
          "Note uploaded successfully! Waiting for admin approval.",
        );
        navigate("/dashboard");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(error.response?.data?.message || "Failed to upload note");
      } finally {
        setUploading(false);
      }
    };

    reader.readAsDataURL(file);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Link
          to="/dashboard"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            color: "#8b5cf6",
            textDecoration: "none",
            marginBottom: "1.5rem",
            fontSize: "0.9rem",
            fontWeight: 500,
            padding: "0.5rem 1rem",
            borderRadius: "10px",
            background: isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.2)"
              : "rgba(139,92,246,0.1)";
            e.target.style.transform = "translateX(-5px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode
              ? "rgba(139,92,246,0.1)"
              : "rgba(139,92,246,0.05)";
            e.target.style.transform = "translateX(0)";
          }}
        >
          <FaArrowLeft /> Back to Dashboard
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            borderRadius: "28px",
            padding: "2rem",
            marginBottom: "2rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  width: "50px",
                  height: "50px",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaUpload size={24} color="white" />
              </div>
              <h1
                style={{
                  fontSize: isMobile ? "1.8rem" : "2rem",
                  fontWeight: "bold",
                  color: "white",
                  margin: 0,
                }}
              >
                Upload <span style={{ color: "#8b5cf6" }}>Notes</span>
              </h1>
            </div>
            <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
              Share your study materials with the VTU community. Your note will
              be reviewed by admin before publication.
            </p>
          </div>
        </motion.div>

        {/* Upload Form */}
        <motion.form
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          onSubmit={handleSubmit}
          style={{
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            padding: isMobile ? "1.5rem" : "2rem",
            borderRadius: "28px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          {/* Dropzone Area */}
          <motion.div
            variants={itemVariants}
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? "#8b5cf6" : getBorderColor()}`,
              borderRadius: "20px",
              padding: "2rem",
              textAlign: "center",
              marginBottom: "2rem",
              cursor: "pointer",
              transition: "all 0.3s",
              background: isDragActive
                ? isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)"
                : getDropzoneBg(),
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto",
                  }}
                >
                  {getFileIcon()}
                </div>
                <p
                  style={{
                    color: getTextColor(),
                    marginTop: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  {file.name}
                </p>
                <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  style={{
                    marginTop: "1rem",
                    padding: "0.3rem 1rem",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "20px",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.3rem",
                  }}
                >
                  <FaTimes size={12} /> Remove File
                </motion.button>
              </motion.div>
            ) : (
              <div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <FaCloudUploadAlt size={60} color="#8b5cf6" />
                </motion.div>
                <p
                  style={{
                    color: getTextColor(),
                    marginTop: "0.5rem",
                    fontWeight: "500",
                  }}
                >
                  {isDragActive
                    ? "Drop the file here"
                    : "Drag & drop a file here, or click to select"}
                </p>
                <p style={{ color: getTextSecondary(), fontSize: "0.85rem" }}>
                  Supported formats: PDF, DOC, DOCX, PPT, PPTX (Max 10MB)
                </p>
              </div>
            )}
          </motion.div>

          {/* Form Fields */}
          <div style={{ display: "grid", gap: "1.5rem" }}>
            <motion.div variants={itemVariants}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Title <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "0.9rem 1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "14px",
                  outline: "none",
                  fontSize: "0.95rem",
                  background: getInputBackground(),
                  color: getTextColor(),
                  transition: "all 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
                placeholder="e.g., Data Structures Complete Notes"
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Description <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                style={{
                  width: "100%",
                  padding: "0.9rem 1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "14px",
                  outline: "none",
                  fontSize: "0.95rem",
                  resize: "vertical",
                  background: getInputBackground(),
                  color: getTextColor(),
                  fontFamily: "inherit",
                  transition: "all 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
                placeholder="Describe what this note contains..."
              />
            </motion.div>

            <motion.div
              variants={itemVariants}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Subject <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.9rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    outline: "none",
                    fontSize: "0.95rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                  placeholder="e.g., Data Structures"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Subject Code <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="text"
                  name="subjectCode"
                  value={formData.subjectCode}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.9rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    outline: "none",
                    fontSize: "0.95rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                    textTransform: "uppercase",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                  placeholder="e.g., 18CS32"
                />
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                gap: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Branch <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.9rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    outline: "none",
                    fontSize: "0.95rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    cursor: "pointer",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                >
                  <option value="">Select Branch</option>
                  {branches.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Semester <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <select
                  name="semester"
                  value={formData.semester}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "0.9rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    outline: "none",
                    fontSize: "0.95rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    cursor: "pointer",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                >
                  <option value="">Select Semester</option>
                  {semesters.map((s) => (
                    <option key={s} value={s}>
                      Semester {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextSecondary(),
                    fontWeight: "500",
                  }}
                >
                  Module
                </label>
                <select
                  name="module"
                  value={formData.module}
                  onChange={handleChange}
                  style={{
                    width: "100%",
                    padding: "0.9rem 1rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "14px",
                    outline: "none",
                    fontSize: "0.95rem",
                    background: getInputBackground(),
                    color: getTextColor(),
                    cursor: "pointer",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                >
                  <option value="">Select Module</option>
                  {modules.map((m) => (
                    <option key={m} value={m}>
                      Module {m}
                    </option>
                  ))}
                </select>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextSecondary(),
                  fontWeight: "500",
                }}
              >
                Tags (comma separated)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.9rem 1rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "14px",
                  outline: "none",
                  fontSize: "0.95rem",
                  background: getInputBackground(),
                  color: getTextColor(),
                  transition: "all 0.3s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                onBlur={(e) => (e.target.style.borderColor = getBorderColor())}
                placeholder="e.g., algorithms, programming, data-structures"
              />
            </motion.div>
          </div>

          {/* Info Notice */}
          <motion.div
            variants={itemVariants}
            style={{
              marginTop: "1.5rem",
              padding: "1rem",
              background: getInfoBg(),
              borderRadius: "16px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <FaInfoCircle size={14} color="#8b5cf6" />
              <p
                style={{
                  color: getTextSecondary(),
                  fontSize: "0.85rem",
                  margin: 0,
                }}
              >
                After upload, your note will be reviewed by admin. You'll see
                status in your dashboard.
              </p>
            </div>
          </motion.div>

          {/* Submit Button */}
          <motion.button
            variants={itemVariants}
            type="submit"
            disabled={uploading}
            whileHover={{ scale: uploading ? 1 : 1.02 }}
            whileTap={{ scale: uploading ? 1 : 0.98 }}
            style={{
              width: "100%",
              padding: "1rem",
              marginTop: "2rem",
              background: uploading
                ? "#94a3b8"
                : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "16px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: uploading ? "not-allowed" : "pointer",
              transition: "all 0.3s",
              boxShadow: uploading
                ? "none"
                : "0 4px 14px 0 rgba(139,92,246,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            {uploading ? (
              <>
                <FaSpinner style={{ animation: "spin 1s linear infinite" }} />{" "}
                Uploading...
              </>
            ) : (
              <>
                <FaCloudUploadAlt /> Upload Note for Review
              </>
            )}
          </motion.button>
        </motion.form>

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "2rem",
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            background: isDarkMode
              ? "rgba(139,92,246,0.05)"
              : "rgba(139,92,246,0.03)",
            borderRadius: "40px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            Your files are securely uploaded and encrypted
          </span>
          <FaStar size={10} color="#f59e0b" />
        </motion.div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default UploadNote;

